import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, EmptyState, FormRow, Notice, PageHeader, Row, SelectField, Stat, StatGrid, TextField } from "@plataforma/design-system";
import { appointmentStatuses, type FeatureKey, type Report, type ReportType } from "@plataforma/shared";
import { useAuth } from "../../app/auth";
import { useAppConfig } from "../../app/config";
import { api, errorMessage, queryString } from "../../lib/api";
import { downloadText, reportToCsv } from "../../lib/csv";
import { monthEnd, monthStart, todayIso } from "../../lib/format";
import { appointmentStatusLabel } from "../../lib/labels";
import { useLocations } from "../agenda/api";

/** Cada relatório depende de uma funcionalidade; só aparece se ela estiver ligada na aplicação. */
const reportOptions: { type: ReportType; label: string; needs: FeatureKey }[] = [
  { type: "appointments", label: "Consultas", needs: "agenda" },
  { type: "finance", label: "Financeiro", needs: "finance" },
  { type: "invoices", label: "Notas fiscais", needs: "invoices" },
  { type: "income-tax", label: "Imposto de renda · apoio", needs: "finance" },
];

export function ReportsPage() {
  const { config, format, has } = useAppConfig();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const options = reportOptions.filter((option) => has(option.needs));
  const requested = options.find((option) => option.type === searchParams.get("tipo"));
  const [type, setType] = useState<ReportType>(requested?.type ?? options[0]?.type ?? "appointments");
  const [from, setFrom] = useState(searchParams.get("de") ?? monthStart(2));
  const [to, setTo] = useState(searchParams.get("ate") ?? monthEnd());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [status, setStatus] = useState("");
  const [locationId, setLocationId] = useState("");
  const [generated, setGenerated] = useState(Boolean(requested));
  const locations = useLocations();
  const annual = type === "income-tax";
  const valid = annual || (Boolean(from && to) && from <= to);
  const params = annual ? { year } : { from, to, ...(type === "appointments" ? { status, locationId } : {}) };
  const report = useQuery({ queryKey: ["report", type, params], enabled: generated && valid, queryFn: async () => (await api.get<{ report: Report }>(`/reports/${type}${queryString(params)}`)).report });
  const cell = (value: string | number) => (typeof value === "number" ? format.money(value) : value);

  if (options.length === 0) return <EmptyState title="Nenhum relatório disponível nesta aplicação" />;
  return (
    <>
      <div data-print="hide">
        <PageHeader title="Relatórios" />
        <Card padded>
          <FormRow>
            <SelectField label="Tipo de relatório" value={type} onChange={(event) => setType(event.target.value as ReportType)}>
              {options.map((option) => <option key={option.type} value={option.type}>{option.label}</option>)}
            </SelectField>
            {annual ? (
              <TextField label="Ano-calendário" type="number" min={2000} max={2100} value={year} onChange={(event) => setYear(event.target.value)} />
            ) : (
              <FormRow>
                <TextField label="De" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
                <TextField label="Até" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
              </FormRow>
            )}
          </FormRow>
          {type === "appointments" ? (
            <FormRow>
              <SelectField label="Situação" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">Todas</option>
                {appointmentStatuses.map((item) => <option key={item} value={item}>{appointmentStatusLabel[item]}</option>)}
              </SelectField>
              <SelectField label="Local" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                <option value="">Todos</option>
                {(locations.data ?? []).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </SelectField>
            </FormRow>
          ) : null}
          <Button variant="primary" onClick={() => setGenerated(true)}>Gerar relatório</Button>
        </Card>
        <div style={{ height: 18 }} />
      </div>
      {!generated ? (
        <EmptyState title="Nenhum relatório gerado">Escolha o tipo e o período e use “Gerar relatório”.</EmptyState>
      ) : !valid ? (
        <Notice tone="danger">Informe um período válido: a data inicial deve ser anterior ou igual à final.</Notice>
      ) : report.isError ? (
        <Notice tone="danger">{errorMessage(report.error)}</Notice>
      ) : !report.data ? (
        <EmptyState>Carregando…</EmptyState>
      ) : (
        <>
          <Row align="between" style={{ marginBottom: 14 }}>
            <div>
              <p className="ds-muted ds-small">{config.name}</p>
              <h2 className="ds-h2">{report.data.title}</h2>
              <p className="ds-muted ds-small">{report.data.period} · gerado em {format.date(todayIso())}{user ? ` por ${user.name}` : ""}</p>
            </div>
            <span className="ds-row" data-print="hide">
              <Button size="small" onClick={() => window.print()}>Imprimir / salvar PDF</Button>
              <Button size="small" onClick={() => downloadText(`${report.data.fileName}.csv`, reportToCsv(report.data), "text/csv;charset=utf-8")}>Exportar CSV</Button>
            </span>
          </Row>
          <StatGrid>{report.data.summary.map((item) => <Stat key={item.label} label={item.label} value={cell(item.value)} />)}</StatGrid>
          {report.data.sections.map((section) => (
            <div key={section.title} style={{ marginBottom: 16 }}>
              <Card title={section.title}>
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead><tr>{section.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
                    <tbody>
                      {section.rows.length === 0 ? <tr><td colSpan={section.columns.length}><EmptyState>Nenhum registro no período.</EmptyState></td></tr> : section.rows.map((row, index) => <tr key={index}>{row.map((value, column) => <td key={column} className={typeof value === "number" ? "ds-num" : undefined}>{cell(value)}</td>)}</tr>)}
                      {section.total && section.rows.length ? <tr data-total="true">{section.total.map((value, column) => <td key={column} className={typeof value === "number" ? "ds-num" : undefined}>{cell(value)}</td>)}</tr> : null}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ))}
          {report.data.notes.map((note) => <p key={note} className="ds-muted ds-small" style={{ marginTop: 6 }}>{note}</p>)}
        </>
      )}
    </>
  );
}
